import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Franchise, Persona, User, Store, District } from '../../../app/services/api/generated';
import { Autocomplete, FormControl, Grid, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import useNswagClient from '../../../hooks/api/useNswagClient';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import useLogError from '../../../hooks/useLogError';
import { UserPutPostRequest } from '../types/UserPutPostRequest';
import { isEmailValid } from '../../../utils';
import { MultiStorePicker, StoreSelection } from '../../../components/shared/MultiStorePicker';

interface SubmitModalProps {
  openModal: boolean;
  onConfirm: Function;
  user: User | undefined;
  onCancel: () => void;
}

const CreateEditModal: React.FC<SubmitModalProps> = ({ openModal, onConfirm, user, onCancel }) => {
  const { t } = useTranslation('manageUsers');
  const { getFranchises, getPersonas, getDistrictsByFranchise, getFranchiseStores } = useNswagClient();
  const [request, setRequest] = useState<UserPutPostRequest | undefined>(undefined);
  const [franchiseList, setFranchiseList] = useState<Franchise[] | undefined>(undefined);
  const [storeSelection, setStoreSelection] = useState<StoreSelection>({ mode: 'all' });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stores, setStores] = useState<Store[] | undefined>([]);
  const [districts, setDistricts] = useState<District[] | undefined>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [allPersonas, setAllPersonas] = useState<Persona[]>([]);
  const { logError } = useLogError();
  const isCreate = !user;
  
  const [selectedFranchises, setSelectedFranchises] = useState<(string | undefined)[]>([]);
  const [isMultiFranchise, setIsMultiFranchise] = useState<boolean>(false);
  const [multiFranchisePersonaId, setMultiFranchisePersonaId] = useState<number | undefined>(undefined);
  
  const MULTI_FRANCHISE_ADMIN_ROLE = 'Multi-Franchise Admin';
  
  useEffect(() => {
    getData();
  }, [user, openModal]);

  useEffect(() => {
    if (isCreate && franchiseList && franchiseList.length === 1) {
      const onlyFranchise = franchiseList[0];
      setSelectedFranchises([onlyFranchise.franchiseName]);
      setRequest(prev => ({
        ...prev,
        franchiseName: onlyFranchise.franchiseName,
        franchiseIds: undefined,
        personaId: undefined,
        districtId: undefined,
        stores: undefined,
      }));
      setPersonas(allPersonas.filter(p => p.franchiseName === onlyFranchise.franchiseName));
      if (onlyFranchise.franchiseName) {
        getDistrictsByFranchiseName(onlyFranchise.franchiseName);
        getStoresByFranchiseName(onlyFranchise.franchiseName);
      }
    }
  }, [isCreate, franchiseList]);

  const getData = () => {
    const newRequest = {
      id: user?.id,
      districtId: user?.district?.id,
      emailAddress: user?.emailAddress,
      allStores: user?.allStores,
      personaId: user?.persona?.id,
      franchiseName: user?.franchiseName,
      stores: user?.stores?.map(x => x.storeNumber) as string[],
    };

    if (user?.district?.id && user?.franchiseName){
      getDistrictsByFranchiseName(user.franchiseName);
    }

    setRequest(newRequest);

    setIsLoading(true);
    getFranchiseData();
    getPersonaData();

    if (user?.franchises && user.franchises.length > 1) {
      const userFranchiseIds = user.franchises.map(f => f.franchiseId);
      setSelectedFranchises(
        franchiseList
          ? franchiseList.filter(f => userFranchiseIds.includes(f.franchiseId)).map(f => f.franchiseName)
          : [],
      );
      setIsMultiFranchise(true);
      setStoreSelection({ mode: 'all' });
    } else if (user?.franchiseName) {
      setSelectedFranchises([user.franchiseName]);
      setIsMultiFranchise(false);
      if (user.allStores) {
        setStoreSelection({ mode: 'all' });
      } else {
        const storeIds = user.stores?.map(x => x.storeNumber) as string[] ?? [];
        setStoreSelection({ mode: 'specific', storeNumbers: storeIds });
      }
      getStoresByFranchiseName(user.franchiseName);
      if (user.district?.id) getDistrictsByFranchiseName(user.franchiseName);
      setPersonas(allPersonas.filter(p => p.franchiseName === user.franchiseName));
    } else {
      setSelectedFranchises([]);
      setIsMultiFranchise(false);
      setStoreSelection({ mode: 'all' });
    }
    setIsLoading(false);
  };
 
  const getPersonaData = () => {
    getPersonas().then((result) => {
      if (result?.data) {
        setAllPersonas(result.data);
        if (
          selectedFranchises.length === 1 &&
          selectedFranchises[0] &&
          selectedFranchises[0].length > 0
        ) {
          setPersonas(result.data.filter(p => p.franchiseName === selectedFranchises[0]));
        } else if (user?.franchiseName && (!user?.franchises || user.franchises.length <= 1)) {
          setPersonas(result.data.filter(p => p.franchiseName === user.franchiseName));
        } else {
          setPersonas(result.data);
        }
        cacheMultiFranchisePersonaId(result.data);
      }
    })
      .catch((error) => {
        logError(error);
      }).finally(() => (setIsLoading(false)));
  };

  const getFranchiseData = () => {
    getFranchises().then((result) => {
      if (result?.data) {
        setFranchiseList(result.data);
      }})
      .catch((error) => {
        logError(error);
      }).finally(() => (setIsLoading(false)));
  };

  const cacheMultiFranchisePersonaId = (personas: Persona[]) => {
    const mfPersona = personas.find(p => p.description === MULTI_FRANCHISE_ADMIN_ROLE);
    if (mfPersona && !multiFranchisePersonaId) {
      setMultiFranchisePersonaId(mfPersona.id);
    }
  };

  const getDistrictsByFranchiseName = (franchiseName: string) => {
    getDistrictsByFranchise(franchiseName).then((result) => {
      if (result?.data) {
        let validDistricts = result?.data as District[];
        validDistricts = validDistricts.filter(x => x.franchiseName == franchiseName);
        setDistricts(validDistricts);
      }})
      .catch((error) => {
        logError(error);
      }).finally(() => (setIsLoading(false)));
  };

  const getStoresByFranchiseName = (franchiseName: string) => {
    getFranchiseStores(franchiseName).then((result) => {
      if (result?.data) {
        setStores(result.data?.stores);
      }})
      .catch((error) => {
        logError(error);
      }).finally(() => (setIsLoading(false)));
  };

  const handleCancel = () => {
    onCancel();
    setRequest(undefined);
  };

  const handleConfirm = async() => {
    const emailValid = await validateEmail(request?.emailAddress ?? '');

    if (emailValid){
      const selectedStores = storeSelection.mode === 'all'
        ? (stores?.map(s => s.storeNumber).filter(sn => sn != null) as string[] || [])
        : storeSelection.storeNumbers;
      const checkedAllStores = storeSelection.mode === 'all';

      // Set franchiseIds only if AllStores is true
      let franchiseIds: number[] | undefined = undefined;
      const shouldSetFranchiseIds = (checkedAllStores && franchiseList);
      if (shouldSetFranchiseIds) {
        franchiseIds = selectedFranchises
          .map(name => franchiseList.find(f => f.franchiseName === name)?.franchiseId)
          .filter((id): id is number => id !== undefined);
      }
      const newRequest = {
        ...request,
        useNewUI: true,
        stores: request?.districtId ? null : selectedStores,
        allStores: request?.districtId ? false : checkedAllStores || isMultiFranchise,
        franchiseName: selectedFranchises.length > 0 ? selectedFranchises[0] : undefined,
        franchiseIds: shouldSetFranchiseIds ? franchiseIds : undefined,
      };
      onConfirm(newRequest);
      setRequest(undefined);
      onCancel();
    }
  };

  const handleFranchisesChange = (
    _event: React.SyntheticEvent,
    newValues: (string | undefined)[],
  ) => {
    setSelectedFranchises(newValues);

    const filteredValues = newValues.filter((v): v is string => v !== undefined);
    // Check if multiple franchises are selected
    const multipleSelected = filteredValues.length > 1;
    setIsMultiFranchise(multipleSelected);

    if (multipleSelected) {
      setStoreSelection({ mode: 'all' });

      let franchiseIds: number[] | undefined = undefined;
      if (franchiseList) {
        franchiseIds = filteredValues
          .map(name => franchiseList.find(f => f.franchiseName === name)?.franchiseId)
          .filter((id): id is number => id !== undefined);
      }
      setRequest({
        ...request,
        personaId: multiFranchisePersonaId,
        districtId: undefined,
        franchiseIds,
        franchiseName: filteredValues.length > 0 ? filteredValues[0] : undefined,
        stores: undefined,
      });
      setStores([]);
      setDistricts([]);
      setPersonas([]);
    } else if (filteredValues.length === 1) {
      const franchiseName = filteredValues[0];
      setRequest(prev => ({
        ...prev,
        franchiseName,
        franchiseIds: undefined,
        personaId: undefined,
        districtId: undefined,
        stores: undefined,
      }));
      setPersonas(allPersonas.filter(p => p.franchiseName === franchiseName));
      getDistrictsByFranchiseName(franchiseName);
      getStoresByFranchiseName(franchiseName);
    } else {
      setRequest({
        ...request,
        franchiseName: undefined,
        franchiseIds: undefined,
        personaId: undefined,
        districtId: undefined,
        stores: undefined,
      });
      setStores([]);
      setDistricts([]);
      setPersonas([]);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRequest({ ...request, [name]: value });
  };

  const handleSelectChange = (event: SelectChangeEvent<number | string>) => {
    const { name, value } = event.target;

    if (name == 'districtId'){
      if (value != undefined){
        setStoreSelection({ mode: 'specific', storeNumbers: [] });
      }
    }
    setRequest({ ...request, [name]: value });
  };

  const validateEmail = (email: string) => {
    const emailValid =  isEmailValid(email);
    return emailValid;
  };

  const formItemStyling = { display: 'flex', flexDirection: 'column', alignItems: 'left'  };
  const isValid = request?.emailAddress != undefined && request?.emailAddress.length > 0 && 
  ((isMultiFranchise && selectedFranchises.length > 1) || 
   (request?.personaId != undefined && selectedFranchises.length === 1 && 
    (request?.districtId != undefined ? request?.districtId > 0 : storeSelection.mode === 'all' || (storeSelection.mode === 'specific' && storeSelection.storeNumbers.length > 0))));
  
  return isLoading ? <LoadingWheel></LoadingWheel> : (
    <Dialog
      onClose={handleCancel}
      aria-labelledby="customized-dialog-title"
      open={openModal}
      sx={{ p: 0 }}
    >
      <DialogTitle sx={{ m: 4, p: 4 }}>
        {t('title')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCancel}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.custom.gray[800],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', maxWidth: '450px' }}>
        <Grid
          container
          spacing={6}
          alignItems='self-end'
          p={6}
        >
          <Grid item
            xs={12}
            sx={formItemStyling}>
            <Typography variant='textSM'>{t('email')}</Typography>
            <TextField
              fullWidth
              required
              name='emailAddress'
              placeholder='Email'
              id="input-with-icon-textfield"
              value={request?.emailAddress}
              disabled={!isCreate}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item
            xs={12}
            sx={formItemStyling}>
            <Typography variant='textSM'>{t('franchise')}</Typography>
            <FormControl>
              <Autocomplete
                multiple
                id="franchises-select"
                options={franchiseList?.map(f => f.franchiseName) || []}
                value={selectedFranchises}
                onChange={handleFranchisesChange}
                renderInput={(params) => (
                  <TextField {...params}
                    placeholder="Select franchises"
                    required />
                )}
                fullWidth
              />
            </FormControl>
          </Grid>
          {districts && districts.length > 0 && !isMultiFranchise &&
          <Grid item
            xs={12}
            sx={formItemStyling}>
            <Typography variant='textSM'>{t('district')}</Typography>
            <FormControl>
              <Select
                name='districtId'
                value={request?.districtId}
                onChange={handleSelectChange}
                fullWidth
                required
                defaultValue={undefined}
              >
                <MenuItem
                  value={undefined}>{t('none')}</MenuItem>
                {districts.map((f) => (
                  <MenuItem key={f.id}
                    value={f.id}>{f.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          }
          {((selectedFranchises.length === 1 && (selectedFranchises[0]?.length ?? 0) > 0) || isMultiFranchise) &&
          <Grid item
            xs={12}
            sx={formItemStyling}>
            <Typography variant='textSM'>{t('role')}</Typography>
            <FormControl>
              {isMultiFranchise ? (
                <TextField
                  value={MULTI_FRANCHISE_ADMIN_ROLE}
                  disabled
                  fullWidth
                />
              ) : (
                <Select
                  name='personaId'
                  value={request?.personaId}
                  onChange={handleSelectChange}
                  fullWidth
                  required
                >
                  {personas && personas.length > 0 && personas.map((f) => (
                    <MenuItem key={f.id}
                      value={f.id}>{f.description}</MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </Grid>
          }
          {!isMultiFranchise &&
          <Grid item
            xs={12}
            sx={formItemStyling}>
            <Typography variant='textSM'>{t('stores')}</Typography>
            <FormControl fullWidth>
              <MultiStorePicker
                stores={stores || []}
                value={storeSelection}
                onChange={setStoreSelection}
                allStoresLabel={t('allStores')}
                disabled={(request?.districtId != undefined && request?.districtId > 0) || selectedFranchises.length !== 1}
              />
            </FormControl>
          </Grid>
          }
        </Grid>
      </DialogContent>
      <DialogActions sx={{ mt: 4, mb: 4, mr: 4 }}>
        <Button variant='secondary'
          size="lg"
          onClick={handleCancel}>
          {t('cancel')}
        </Button>
        <Button
          variant='primary'
          size="lg"
          disabled={!isValid}
          onClick={handleConfirm}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CreateEditModal;